import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import { Ticket } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { DataGrid } from '@/components/ui/DataGrid';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormLayout';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { Can } from '@/lib/permissions';
import { formatCents, formatDateTime, formatShortId } from '@/lib/format';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import {
  loyaltyLedgerReasonLabel,
  voucherDiscountLabel,
  voucherStatusLabel,
  voucherStatusTone,
} from '@/lib/labels';
import type { UserLoyaltyQuery } from '@/gql/graphql';
import {
  AdjustUserPointsDoc,
  AssignGoldenTicketDoc,
  RevokeGoldenTicketDoc,
  UserGoldenTicketsDoc,
} from './userDocuments';

type Loyalty = NonNullable<UserLoyaltyQuery['userLoyalty']>;
type LedgerLine = Loyalty['recentLedger'][number];

export interface GoldenTicketRef {
  id: string;
  userId: string;
  holderName: string;
  holderEmail?: string | null;
  status: string;
  assignedAt: string;
  note?: string | null;
  profileComplete: boolean;
}

export interface UserLoyaltyTabProps {
  userId: string;
  email: string;
  loyalty?: Loyalty | null;
  loading: boolean;
  error?: unknown;
  goldenTicket: GoldenTicketRef | null;
  onRefetchLoyalty: () => Promise<unknown>;
  onGoldenChange: () => Promise<unknown>;
}

/** Points ledger, minted vouchers, and the personal golden ticket. */
export function UserLoyaltyTab({
  userId,
  email,
  loyalty,
  loading,
  error,
  goldenTicket,
  onRefetchLoyalty,
  onGoldenChange,
}: UserLoyaltyTabProps) {
  const goldenQuery = useQuery(UserGoldenTicketsDoc, {
    variables: { filter: { search: email, status: ['ACTIVE'] } },
    errorPolicy: 'all',
  });

  const activeGolden =
    goldenTicket?.status === 'ACTIVE'
      ? goldenTicket
      : (goldenQuery.data?.goldenTickets.nodes.find(
          (g) => g.userId === userId && g.status === 'ACTIVE',
        ) ?? null);

  const [pointsDelta, setPointsDelta] = useState('0');
  const [pointsNote, setPointsNote] = useState('');
  const [pointsError, setPointsError] = useState<string | null>(null);
  const [goldenNote, setGoldenNote] = useState('');
  const [goldenError, setGoldenError] = useState<string | null>(null);
  const [goldenMailed, setGoldenMailed] = useState(false);

  const [adjustPoints, { loading: adjusting }] =
    useMutation(AdjustUserPointsDoc);
  const [assignGolden, { loading: assigning }] = useMutation(
    AssignGoldenTicketDoc,
  );
  const [revokeGolden, { loading: revokingGolden }] = useMutation(
    RevokeGoldenTicketDoc,
  );

  const ledgerColumns = useMemo<ColumnDef<LedgerLine>[]>(
    () => [
      {
        id: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {loyaltyLedgerReasonLabel(row.original.reason)}
              {row.original.scope ? ` · ${row.original.scope.name}` : ''}
            </p>
            {row.original.note ? (
              <p className="text-xs text-gray-500">{row.original.note}</p>
            ) : null}
          </div>
        ),
      },
      {
        id: 'points',
        header: 'Points',
        enableSorting: false,
        cell: ({ row }) => {
          const pts = row.original.points;
          const signed = pts > 0 ? `+${pts}` : String(pts);
          return (
            <span
              className={
                pts < 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-900 dark:text-white'
              }
            >
              {signed}
            </span>
          );
        },
      },
      {
        id: 'date',
        header: 'Date',
        enableSorting: false,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'order',
        header: 'Order',
        enableSorting: false,
        cell: ({ row }) => {
          const orderId = row.original.orderId
            ? String(row.original.orderId)
            : '';
          return orderId ? (
            <Link
              to={`/orders/${orderId}`}
              className="text-brand hover:underline"
            >
              #{formatShortId(orderId)}
            </Link>
          ) : (
            '—'
          );
        },
      },
      {
        id: 'payment',
        header: 'Payment',
        enableSorting: false,
        cell: ({ row }) => {
          const paymentId = row.original.paymentId
            ? String(row.original.paymentId)
            : '';
          const refundId = row.original.refundId
            ? String(row.original.refundId)
            : '';
          if (paymentId) {
            return (
              <Link
                to={`/payments?search=${encodeURIComponent(paymentId)}`}
                className="text-brand hover:underline"
              >
                #{formatShortId(paymentId)}
              </Link>
            );
          }
          if (refundId) {
            return (
              <Link
                to={`/refunds?search=${encodeURIComponent(refundId)}`}
                className="text-brand hover:underline"
              >
                Refund #{formatShortId(refundId)}
              </Link>
            );
          }
          return '—';
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      {error ? (
        <QueryErrorNotice
          error={error}
          title="Could not load loyalty details"
        />
      ) : null}

      <Can permission="loyalty:adjust">
        <Card
          title="Manual point adjustment"
          subtitle="Requires a passkey session (loyalty:adjust)."
        >
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={async (event) => {
              event.preventDefault();
              setPointsError(null);
              const pointsValue = Number.parseInt(pointsDelta, 10);
              if (!Number.isFinite(pointsValue) || pointsValue === 0) {
                setPointsError('Enter a non-zero signed integer.');
                return;
              }
              if (!pointsNote.trim()) {
                setPointsError('A justification note is required.');
                return;
              }
              try {
                await adjustPoints({
                  variables: {
                    input: {
                      userId,
                      points: pointsValue,
                      note: pointsNote.trim(),
                    },
                  },
                });
                setPointsDelta('0');
                setPointsNote('');
                await onRefetchLoyalty();
              } catch (err) {
                setPointsError(graphqlErrorMessage(err));
              }
            }}
          >
            <FormField label="Points (signed)">
              <Input
                type="number"
                value={pointsDelta}
                onChange={(e) => setPointsDelta(e.target.value)}
              />
            </FormField>
            <FormField label="Note" className="min-w-64 flex-1">
              <Input
                value={pointsNote}
                onChange={(e) => setPointsNote(e.target.value)}
                required
              />
            </FormField>
            <Button type="submit" loading={adjusting}>
              Adjust
            </Button>
          </form>
          {pointsError ? (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {pointsError}
            </p>
          ) : null}
        </Card>
      </Can>

      <Card
        title="Recent points ledger"
        subtitle="Earn, refund, and reward-spend lines. Threshold vouchers debit their snapshotted cost at redeem."
      >
        {loading && !loyalty ? (
          <p className="text-sm text-gray-500">Loading ledger…</p>
        ) : (loyalty?.recentLedger.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-500">No ledger lines yet.</p>
        ) : (
          <DataGrid<LedgerLine>
            data={loyalty?.recentLedger ?? []}
            columns={ledgerColumns}
            rowId={(row) => String(row.id)}
            emptyState={
              <p className="text-sm text-gray-500">No ledger lines yet.</p>
            }
          />
        )}
      </Card>

      <Card
        title="Vouchers"
        subtitle="Minted rewards this user holds. Not bought and not priced in points."
      >
        {(loyalty?.vouchers.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-500">No vouchers.</p>
        ) : (
          <ul className="divide-y divide-gray-200 text-sm dark:divide-gray-800">
            {loyalty?.vouchers.map((voucher) => (
              <li
                key={voucher.id}
                className="flex items-center justify-between py-2"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {voucher.label ||
                      voucherDiscountLabel(voucher.discountType)}
                    {voucher.discountPercentage != null
                      ? ` · ${voucher.discountPercentage}%`
                      : null}
                    {voucher.discountAmountCents != null
                      ? ` · ${formatCents(voucher.discountAmountCents as number)}`
                      : null}
                    {voucher.productName ? ` · ${voucher.productName}` : null}
                    {voucher.ruleName ? ` · ${voucher.ruleName}` : null}
                  </p>
                  <p className="text-xs text-gray-500">
                    {voucher.scope.name} · issued{' '}
                    {formatDateTime(voucher.issuedAt)}
                  </p>
                </div>
                <Badge tone={voucherStatusTone(voucher.status)}>
                  {voucherStatusLabel(voucher.status)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Can
        permission="golden_ticket:manage"
        fallback={
          <Can permission="golden_ticket:read">
            <Card
              title="Golden ticket"
              subtitle="Personal pass — not a per-night ticket, never emailed."
            >
              {goldenQuery.error ? (
                <QueryErrorNotice error={goldenQuery.error} />
              ) : activeGolden ? (
                <p className="text-sm">
                  Active since {formatDateTime(activeGolden.assignedAt)}
                  {activeGolden.profileComplete
                    ? ''
                    : ' · profile incomplete (QR blocked in ExpoApp)'}
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  No active golden ticket.
                </p>
              )}
            </Card>
          </Can>
        }
      >
        <Card
          title="Golden ticket"
          subtitle="One active golden ticket per user. Assign and revoke require a passkey session."
          actions={
            activeGolden ? (
              <Button
                type="button"
                variant="danger"
                size="sm"
                loading={revokingGolden}
                onClick={async () => {
                  setGoldenError(null);
                  try {
                    await revokeGolden({
                      variables: {
                        id: activeGolden.id,
                        note: goldenNote || null,
                      },
                    });
                    setGoldenMailed(false);
                    await goldenQuery.refetch();
                    await onGoldenChange();
                  } catch (err) {
                    setGoldenError(graphqlErrorMessage(err));
                  }
                }}
              >
                Revoke
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                leftIcon={<Ticket className="h-4 w-4" />}
                loading={assigning}
                onClick={async () => {
                  setGoldenError(null);
                  try {
                    await assignGolden({
                      variables: { userId, note: goldenNote || null },
                    });
                    setGoldenMailed(email.trim() !== '');
                    await goldenQuery.refetch();
                    await onGoldenChange();
                  } catch (err) {
                    setGoldenError(graphqlErrorMessage(err));
                    // Backend may already have a ticket (e.g. assigned outside
                    // this tab); refresh so the card doesn't stay on "No active
                    // golden ticket".
                    await goldenQuery.refetch();
                    await onGoldenChange();
                  }
                }}
              >
                Assign
              </Button>
            )
          }
        >
          {goldenQuery.error ? (
            <QueryErrorNotice error={goldenQuery.error} />
          ) : activeGolden ? (
            <p className="text-sm text-gray-700 dark:text-gray-200">
              Active since {formatDateTime(activeGolden.assignedAt)}
              {activeGolden.profileComplete
                ? ''
                : ' · profile incomplete (ExpoApp QR sheet is blocked until last name and date of birth are filled).'}
            </p>
          ) : (
            <p className="text-sm text-gray-500">No active golden ticket.</p>
          )}
          {goldenMailed ? (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
              We mailed them to open the app.
            </p>
          ) : null}
          <FormField label="Note" className="mt-3">
            <Input
              value={goldenNote}
              onChange={(e) => setGoldenNote(e.target.value)}
              placeholder="Optional justification"
            />
          </FormField>
          {goldenError ? (
            <p className="mt-2 text-sm text-red-600">{goldenError}</p>
          ) : null}
        </Card>
      </Can>
    </div>
  );
}
